import { useEffect, useMemo, useState } from 'react';

import {
    calculationRequestFilterOptions,
    calculationRequestTableDescriptor,
} from '@/components/features/CalculationRequests/tables/calculation-request-table-descriptor';
import { CalculationRequestCard } from '@/components/features/CalculationRequests/tables/CalculationRequestCard';
import { CalculationRequestTable } from '@/components/features/CalculationRequests/tables/CalculationRequestTable';
import type { CalculationRequest, CalculationRequestMetrics, CalculationRequestOptions } from '@/components/features/CalculationRequests/types';
import { CreateButton } from '@/components/layout/CreateButton';
import { PageContent } from '@/components/layout/PageContent';
import { PageHeader } from '@/components/layout/PageHeader';
import { SummaryMetricCard } from '@/components/ui/analytics/SummaryMetricCard';
import {
    buildToolbarDefinitions,
    columnsRevealedByFilters,
    quickFilterButtonLabel,
    serverSortKey,
    toFilterSpecMap,
} from '@/components/ui/embedded-resource-table/descriptor';
import { GenericToolbar, type ToolbarViewMode } from '@/components/ui/generic-toolbar/GenericToolbar';
import { PaginationControls } from '@/components/ui/pagination-controls';
import { useColumnVisibility } from '@/hooks/use-column-visibility';
import { useDebounce } from '@/hooks/use-debounce';
import { useIndexFilters } from '@/hooks/use-index-filters';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTranslations } from '@/hooks/useTranslations';
import AppLayout from '@/layouts/app-layout';
import type { AssignableUser } from '@/lib/assigned-user-options';
import { formatCurrency } from '@/lib/utils';
import { type PaginationMeta, type User } from '@/types';
import { Head, router, usePage } from '@inertiajs/react';
import { AlertTriangle, Calculator, Clock, Inbox, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

interface CalculationRequestsPageProps {
    requests: CalculationRequest[];
    pagination: PaginationMeta;
    metrics: CalculationRequestMetrics;
    filters: Record<string, string | string[] | number | boolean | undefined>;
    options: CalculationRequestOptions;
    availableFilterValues?: Record<string, string[]>;
    /** Every active colleague, with their roles — what the created-by filter groups by. */
    filterUsers?: AssignableUser[];
    currentUser: User;
    [key: string]: unknown;
}

const COLUMN_STORAGE_KEY = 'calculation-requests:index';

export default function CalculationRequestsIndex() {
    const { props } = usePage<CalculationRequestsPageProps>();
    const { requests, pagination, metrics, filters, options, availableFilterValues, filterUsers = [] } = props;
    const { t } = useTranslations();

    // Columns, filters and sort entries come from the descriptor the employee
    // profile's calculations tab reads too, so the two cannot drift apart.
    const definitions = useMemo(() => buildToolbarDefinitions(calculationRequestTableDescriptor, t, { requireServerSort: true }), [t]);
    const filterSpecs = useMemo(() => toFilterSpecMap(calculationRequestTableDescriptor), []);

    const {
        columnFilters,
        handleFilterChange,
        toggleSingleFilter,
        clearFilters: clearServerFilters,
        applyFilters,
        loading,
    } = useIndexFilters(filterSpecs, filters, '/calculation-requests');

    const isMobile = useIsMobile();
    const [viewMode, setViewMode] = useState<ToolbarViewMode>(isMobile ? 'grid' : 'table');
    const [searchValue, setSearchValue] = useState(filters.search?.toString() ?? '');
    const debouncedSearchValue = useDebounce(searchValue, 800);
    const [columnFilterSearch, setColumnFilterSearch] = useState<Record<string, string>>({});
    const [sortColumn, setSortColumn] = useState<string | null>((filters.sort_by as string) ?? null);
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>((filters.sort_order as 'asc' | 'desc') ?? null);
    const {
        visibleColumns,
        showColumn: handleColumnShow,
        hideColumn: handleColumnHide,
    } = useColumnVisibility(COLUMN_STORAGE_KEY, definitions.defaultVisibleColumns, definitions.allColumnIds);
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [isSortOpen, setIsSortOpen] = useState(false);

    useEffect(() => {
        setViewMode(isMobile ? 'grid' : 'table');
    }, [isMobile]);

    useEffect(() => {
        const nextSearch = filters.search?.toString() ?? '';
        setSearchValue((previous) => (previous === nextSearch ? previous : nextSearch));
    }, [filters.search]);

    useEffect(() => {
        if ((filters.search?.toString() ?? '') === debouncedSearchValue) {
            return;
        }

        applyFilters({ search: debouncedSearchValue || undefined, page: 1 });
    }, [applyFilters, debouncedSearchValue, filters.search]);

    // Only companies that actually have calculation requests, scoped to what this
    // viewer can see.
    const companyOptions = useMemo(() => {
        const availableIds = new Set(availableFilterValues?.company_id ?? []);

        return availableIds.size ? options.companies.filter((company) => availableIds.has(company.id)) : options.companies;
    }, [options.companies, availableFilterValues]);

    const columnFilterOptions = useMemo(
        () =>
            calculationRequestFilterOptions({
                t,
                statuses: options.statuses,
                phases: options.phases,
                complexities: options.complexities,
                outcomes: options.outcomes,
                engineers: options.engineers,
                submitters: filterUsers,
                companies: companyOptions,
            }),
        [t, options.statuses, options.phases, options.complexities, options.outcomes, options.engineers, filterUsers, companyOptions],
    );

    // Complexity and outcome are filterable but off by default, so narrowing by
    // one used to hide the very thing you narrowed by.
    const effectiveVisibleColumns = useMemo(() => {
        const revealed = columnsRevealedByFilters(calculationRequestTableDescriptor, columnFilters);

        return revealed.every((id) => visibleColumns.has(id)) ? visibleColumns : new Set([...visibleColumns, ...revealed]);
    }, [columnFilters, visibleColumns]);

    const handleSortChange = (column: string, direction: 'asc' | 'desc') => {
        setSortColumn(column);
        setSortDirection(direction);
        applyFilters({ sort_by: serverSortKey(calculationRequestTableDescriptor, column) ?? column, sort_order: direction, page: 1 });
    };

    const clearSort = () => {
        setSortColumn(null);
        setSortDirection(null);
        applyFilters({ sort_by: undefined, sort_order: undefined, page: 1 });
    };

    const clearFilters = () => {
        setSearchValue('');
        // The spec map covers the checkbox filters; these two are page-local.
        clearServerFilters({ date_from: undefined, date_to: undefined, overdue_only: undefined });
    };

    // Inline edits: PATCH, then let Inertia refresh the page props in place.
    const handleUpdate = {
        status: async (id: string, value: string) => {
            router.patch(
                `/calculation-requests/${id}/status`,
                { status: value },
                {
                    preserveState: true,
                    preserveScroll: true,
                    onSuccess: () => toast.success(t('calculation_requests.index.messages.status_updated', 'Status updated')),
                    // The server explains a refused start ("only engineers can…"); a
                    // generic failure message would hide the actual reason.
                    onError: (errors) =>
                        toast.error(errors.status ?? t('calculation_requests.index.messages.failed_update_status', 'Failed to update status')),
                },
            );
        },
        assignee: async (id: string, userId: string | null) => {
            router.patch(
                `/calculation-requests/${id}/assign`,
                { assigned_to_user_id: userId },
                {
                    preserveState: true,
                    preserveScroll: true,
                    onSuccess: () => toast.success(t('calculation_requests.index.messages.assignee_updated', 'Engineer updated')),
                    onError: () => toast.error(t('calculation_requests.index.messages.failed_update_assignee', 'Failed to update engineer')),
                },
            );
        },
    };

    const breadcrumbs = [
        {
            title: t('calculation_requests.index.breadcrumbs.calculation_requests', 'Calculation Requests'),
            href: '/calculation-requests',
        },
    ];

    const restricted = t('calculation_requests.index.metrics.restricted', 'Restricted');

    return (
        <AppLayout breadcrumbs={breadcrumbs}>
            <Head title={t('calculation_requests.index.title', 'Calculation Requests')} />

            <PageHeader
                title={t('calculation_requests.index.heading', 'Calculation Requests')}
                subtitle={t('calculation_requests.index.subheading', 'Engineering calculation queue')}
                actions={
                    <CreateButton
                        href="/calculation-requests/create"
                        permission="calculation-requests.create"
                        label={t('calculation_requests.index.actions.create', 'New request')}
                    />
                }
            />

            <PageContent className="space-y-5">
                {/* Metrics reflect the active filters, not the whole table. */}
                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
                    <SummaryMetricCard
                        title={t('calculation_requests.index.metrics.open', 'In queue')}
                        value={metrics.open}
                        subtitle={t('calculation_requests.index.metrics.open_hint', 'Submitted, being calculated or paused')}
                        icon={<Inbox className="text-muted-foreground h-4 w-4" />}
                    />
                    <SummaryMetricCard
                        title={t('calculation_requests.index.metrics.overdue', 'Overdue')}
                        value={metrics.overdue}
                        subtitle={t('calculation_requests.index.metrics.overdue_hint', 'Past the requested date')}
                        icon={<AlertTriangle className="text-muted-foreground h-4 w-4" />}
                        intent={metrics.overdue > 0 ? 'danger' : 'neutral'}
                    />
                    <SummaryMetricCard
                        title={t('calculation_requests.index.metrics.calculated', 'Calculated')}
                        value={metrics.can_view_financials ? formatCurrency(metrics.calculated_total ?? 0) : restricted}
                        subtitle={t('calculation_requests.index.metrics.calculated_hint', 'Total value of completed calculations')}
                        icon={<Calculator className="text-muted-foreground h-4 w-4" />}
                    />
                    <SummaryMetricCard
                        title={t('calculation_requests.index.metrics.sold', 'Sold')}
                        value={metrics.can_view_financials ? formatCurrency(metrics.sold_total ?? 0) : restricted}
                        subtitle={t('calculation_requests.index.metrics.sold_hint', 'Value of calculations that became projects')}
                        icon={<TrendingUp className="text-muted-foreground h-4 w-4" />}
                        intent="success"
                    />
                    <SummaryMetricCard
                        title={
                            metrics.can_view_financials
                                ? t('calculation_requests.index.metrics.hit_rate', 'Hit rate')
                                : t('calculation_requests.index.metrics.avg_turnaround', 'Avg turnaround')
                        }
                        value={
                            metrics.can_view_financials
                                ? metrics.hit_rate === null
                                    ? '—'
                                    : `${metrics.hit_rate}%`
                                : metrics.avg_turnaround_days === null
                                  ? '—'
                                  : `${metrics.avg_turnaround_days} ${t('calculation_requests.index.metrics.days', 'days')}`
                        }
                        subtitle={
                            metrics.can_view_financials
                                ? t('calculation_requests.index.metrics.hit_rate_hint', 'Sold as a share of calculated')
                                : t('calculation_requests.index.metrics.avg_turnaround_hint', 'Submission to completion')
                        }
                        icon={<Clock className="text-muted-foreground h-4 w-4" />}
                    />
                </div>

                <GenericToolbar
                    quickFilterButtonLabel={quickFilterButtonLabel(calculationRequestTableDescriptor, t, columnFilters)}
                    quickFilterDefinitions={definitions.quickFilterDefinitions}
                    columnDefinitions={definitions.columnDefinitions}
                    extraFilterDefinitions={definitions.extraFilterDefinitions}
                    columnFilterOptions={columnFilterOptions}
                    columnFilters={columnFilters}
                    columnFilterSearch={columnFilterSearch}
                    setColumnFilterSearch={setColumnFilterSearch}
                    toggleSingleFilter={toggleSingleFilter}
                    handleFilterChange={handleFilterChange}
                    clearFilters={clearFilters}
                    isFilterOpen={isFilterOpen}
                    setIsFilterOpen={setIsFilterOpen}
                    isSortOpen={isSortOpen}
                    setIsSortOpen={setIsSortOpen}
                    sortColumn={sortColumn}
                    sortDirection={sortDirection}
                    handleSortChange={handleSortChange}
                    clearSort={clearSort}
                    search={searchValue}
                    setSearch={setSearchValue}
                    perPage={pagination.per_page}
                    onPerPageChange={(perPage: number) => applyFilters({ per_page: perPage, page: 1 })}
                    viewMode={viewMode}
                    setViewMode={setViewMode}
                    visibleColumns={visibleColumns}
                    handleColumnShow={handleColumnShow}
                    handleColumnHide={handleColumnHide}
                    filteredCount={pagination.total}
                    resourceName={calculationRequestTableDescriptor.resourceName}
                    resourceNamePlural={t('calculation_requests.index.title', 'Calculation Requests').toLowerCase()}
                    sortableColumns={definitions.sortableColumns}
                />

                {viewMode === 'grid' ? (
                    requests.length === 0 ? (
                        <div className="text-muted-foreground rounded-xl border border-dashed py-12 text-center text-sm">
                            {t('calculation_requests.index.empty.title', 'No calculation requests')}
                        </div>
                    ) : (
                        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                            {requests.map((request) => (
                                <CalculationRequestCard
                                    key={request.id}
                                    request={request}
                                    options={options}
                                    canViewFinancials={metrics.can_view_financials}
                                />
                            ))}
                        </div>
                    )
                ) : (
                    <CalculationRequestTable
                        requests={requests}
                        onUpdate={handleUpdate}
                        options={options}
                        canViewFinancials={metrics.can_view_financials}
                        sortColumn={sortColumn}
                        sortDirection={sortDirection}
                        onSortChange={handleSortChange}
                        onColumnHide={handleColumnHide}
                        visibleColumns={effectiveVisibleColumns}
                        columnFilters={columnFilters}
                        columnFilterOptions={columnFilterOptions}
                        onFilterChange={handleFilterChange}
                    />
                )}

                <PaginationControls
                    pagination={pagination}
                    loading={loading}
                    onPageChange={(page: number) => applyFilters({ page })}
                    onPerPageChange={(perPage: number) => applyFilters({ per_page: perPage, page: 1 })}
                />
            </PageContent>
        </AppLayout>
    );
}
